import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Utensils, Smile, Stethoscope, Wrench, Briefcase, CheckCircle2,
  ArrowRight, Sparkles, Users, Target, ClipboardList, Award,
} from "lucide-react";
import { goPack, goTiers, apiMe, goRegenPackHero } from "@/lib/api";
import { API } from "@/lib/api";
import { toast } from "sonner";

const ICONS = {
  restaurant: Utensils, dentist: Smile, "medical-clinic": Stethoscope,
  "home-services": Wrench, "professional-services": Briefcase,
};

/** Industry Growth Pack deep page — one route handler for every pack.
 *  Renders hero promise, workflows, agents, KPIs, illustrative case study,
 *  pricing add-on details and a clear conversion CTA to the Blueprint. */

export default function GrowthOperatorPack() {
  const { slug } = useParams();
  const [pack, setPack] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [heroKey, setHeroKey] = useState(0);
  const [styleTweak, setStyleTweak] = useState("");

  useEffect(() => {
    goPack(slug).then(setPack).catch(() => setNotFound(true));
    goTiers().then(r => setTiers(r.tiers || []));
    apiMe().then((u) => setIsAdmin(["admin", "superadmin"].includes(u?.role))).catch(() => {});
    document.title = `${slug.replace(/-/g, " ")} Growth Pack — WebsiteTech Growth Operator`;
  }, [slug]);

  const regen = async () => {
    if (!window.confirm(`Regenerate the ${slug} hero photo? Takes 10-20 seconds.`)) return;
    setRegenerating(true);
    try {
      const r = await goRegenPackHero(slug, styleTweak.trim());
      setHeroKey((k) => k + 1);
      if (r?.style_ignored) {
        toast.info("New hero photo generated (style hint was ignored for policy reasons).");
      } else if (r?.style_tweak_used) {
        toast.success(`New hero photo generated with style: "${r.style_tweak_used}"`);
      } else {
        toast.success("New hero photo generated");
      }
      setStyleTweak("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally { setRegenerating(false); }
  };

  if (notFound) return <Navigate to="/growth-operator" replace />;
  if (!pack) return (
    <div className="min-h-screen bg-white"><Navbar /><div className="p-10 text-slate-500">Loading pack…</div><Footer /></div>
  );

  const Icon = ICONS[pack.slug] || Briefcase;
  const showTiers = tiers.slice(0, 3);

  return (
    <div className="min-h-screen bg-white" data-testid={`pack-page-${pack.slug}`}>
      <Navbar />

      {isAdmin && (
        <div className="border-b border-amber-200 bg-amber-50" data-testid="pack-admin-bar">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2 px-4 py-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-amber-800">
              Admin controls · Pack: {pack.slug}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={styleTweak}
                onChange={(e) => setStyleTweak(e.target.value.slice(0, 200))}
                placeholder='Style tweak (e.g. "less pastel, more warm light")'
                className="h-8 w-64 rounded-md border border-amber-300 bg-white px-2 text-xs text-slate-800 placeholder:text-slate-400 focus:border-amber-500 focus:outline-none"
                data-testid={`pack-style-tweak-${pack.slug}`}
                maxLength={200}
              />
              <Button
                size="sm" variant="outline"
                onClick={regen}
                disabled={regenerating}
                className="border-amber-300 bg-white hover:bg-amber-100"
                data-testid={`pack-regen-hero-${pack.slug}`}
              >
                {regenerating ? "Regenerating…" : "Regenerate hero photo"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-br from-orange-50 via-white to-sky-50">
        {/* Pack-specific hero photograph (Nano Banana, cached) */}
        <img
          src={`${API}/growth-operator/industry-packs/${pack.slug}/hero.png?v=${heroKey}`}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
          data-testid={`pack-hero-image-${pack.slug}`}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        {/* Soft warm gradient so text stays readable over any photo */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/70 to-white/20" />
        <div className="absolute inset-0 opacity-60" style={{
          backgroundImage: "radial-gradient(circle at 12% 20%, rgba(251,146,60,.18), transparent 45%), radial-gradient(circle at 85% 75%, rgba(56,189,248,.10), transparent 45%)",
        }} />
        <div className="relative mx-auto max-w-5xl px-4 py-16 sm:py-20">
          <Link to="/growth-operator" className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-orange-600 hover:text-orange-500">
            ← All Growth Packs
          </Link>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-orange-700 backdrop-blur">
            <Icon className="h-3.5 w-3.5" /> {pack.name} Growth Pack
          </div>
          <h1 className="mt-5 max-w-3xl font-heading text-4xl font-black leading-tight text-slate-900 sm:text-5xl">
            {pack.hero_promise}
          </h1>
          <p className="mt-4 max-w-2xl text-slate-700">
            <b>The problem this pack solves:</b> {pack.problem}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button asChild size="lg" className="h-12 bg-orange-500 px-6 text-base font-bold text-white shadow-md shadow-orange-500/20 hover:bg-orange-400" data-testid={`pack-cta-blueprint-${pack.slug}`}>
              <Link to="/growth-operator/blueprint">Build My Free Growth Blueprint <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="h-12 border-slate-300 bg-white/70 text-slate-800 hover:bg-white">
              <a href="#pack-tiers">See pricing</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Workflows */}
      <section className="mx-auto max-w-5xl px-4 py-16" data-testid={`pack-workflows-${pack.slug}`}>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
          <ClipboardList className="h-4 w-4" /> What we operate for you
        </div>
        <h2 className="mt-2 font-heading text-3xl font-black text-slate-900">The core workflows in this pack</h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {pack.workflows.map((w, i) => (
            <Card key={i} className="border-slate-200 p-5" data-testid={`pack-workflow-${pack.slug}-${i}`}>
              <div className="flex items-start gap-3">
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-orange-100 font-heading text-sm font-black text-orange-700">{i + 1}</div>
                <div className="text-sm text-slate-700">{w}</div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Agents + KPIs */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-5xl px-4 py-16 grid gap-8 md:grid-cols-2">
          <Card className="border-slate-200 p-6" data-testid={`pack-agents-${pack.slug}`}>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
              <Users className="h-4 w-4" /> Specialist agents
            </div>
            <h3 className="mt-2 font-heading text-xl font-bold text-slate-900">Your on-call team</h3>
            <ul className="mt-4 space-y-2">
              {pack.agents.map((a, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{a}
                </li>
              ))}
            </ul>
          </Card>
          <Card className="border-slate-200 p-6" data-testid={`pack-kpis-${pack.slug}`}>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
              <Target className="h-4 w-4" /> KPIs we track
            </div>
            <h3 className="mt-2 font-heading text-xl font-bold text-slate-900">Outcomes, not vanity metrics</h3>
            <ul className="mt-4 space-y-2">
              {pack.kpis.map((k, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />{k}
                </li>
              ))}
            </ul>
            <div className="mt-4 text-[11px] text-slate-500">
              Every reported number labels what is measured versus estimated.
            </div>
          </Card>
        </div>
      </section>

      {/* Case study */}
      {pack.case_study && (
        <section className="mx-auto max-w-5xl px-4 py-16" data-testid={`pack-case-study-${pack.slug}`}>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <Award className="h-4 w-4" /> Illustrative case study
          </div>
          <Card className="mt-4 border-slate-200 p-6">
            <div className="font-heading text-xl font-bold text-slate-900">{pack.case_study.customer}</div>
            <div className="mt-3 grid gap-4 sm:grid-cols-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Problem</div>
                <div className="mt-1 text-sm text-slate-700">{pack.case_study.problem}</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Period</div>
                <div className="mt-1 text-sm text-slate-700">{pack.case_study.period}</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Outcome</div>
                <div className="mt-1 text-sm text-slate-700">{pack.case_study.outcome}</div>
              </div>
            </div>
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <b>Disclaimer:</b> {pack.case_study.disclaimer}
            </div>
          </Card>
        </section>
      )}

      {/* Tiers with pack pricing */}
      <section id="pack-tiers" className="border-y border-slate-200 bg-slate-50" data-testid={`pack-tiers-${pack.slug}`}>
        <div className="mx-auto max-w-5xl px-4 py-16">
          <h2 className="font-heading text-3xl font-black text-slate-900">Pricing for this pack</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            The {pack.name} pack is included with every Growth Operator tier.
            {pack.pricing_addon?.setup_usd > 0 && (
              <> A one-time <b>${pack.pricing_addon.setup_usd}</b> setup applies for compliance & configuration.</>
            )}
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {showTiers.map((t, i) => (
              <Card key={t.id} className={`relative border-slate-200 bg-white p-6 ${i === 1 ? "ring-2 ring-orange-400" : ""}`} data-testid={`pack-tier-${pack.slug}-${t.id}`}>
                {i === 1 && (
                  <div className="absolute -top-3 left-4 rounded-full bg-orange-500 px-3 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                    Most popular
                  </div>
                )}
                <div className="font-heading text-lg font-black text-slate-900">{t.name}</div>
                <div className="mt-1 text-xs text-slate-500">{t.tagline}</div>
                <div className="mt-3 font-heading text-2xl font-black text-orange-600">{t.price_display}</div>
                {pack.pricing_addon?.setup_usd > 0 && (
                  <div className="mt-1 text-xs text-slate-500">+ ${pack.pricing_addon.setup_usd} one-time setup for this pack</div>
                )}
                <ul className="mt-4 space-y-2">
                  {t.includes.slice(0, 4).map((inc, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{inc}
                    </li>
                  ))}
                </ul>
                <Button asChild className="mt-5 w-full bg-slate-900 text-white hover:bg-slate-800">
                  <Link to="/growth-operator/blueprint">Start with a free Blueprint</Link>
                </Button>
              </Card>
            ))}
          </div>
          {pack.pricing_addon?.note && (
            <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 text-xs text-slate-600">
              {pack.pricing_addon.note}
            </div>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-slate-200 bg-gradient-to-br from-white via-orange-50 to-amber-50">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center">
          <Sparkles className="mx-auto h-8 w-8 text-orange-500" />
          <h2 className="mt-4 font-heading text-3xl font-black text-slate-900 sm:text-4xl">
            See what the {pack.name} pack does for you.
          </h2>
          <p className="mt-3 text-slate-700">
            Free Growth Blueprint: readiness score, 30/60/90-day plan, tier fit. About 3 minutes.
          </p>
          <Button asChild size="lg" className="mt-8 h-12 bg-orange-500 px-8 text-base font-bold text-white shadow-md shadow-orange-500/20 hover:bg-orange-400" data-testid={`pack-cta-blueprint-final-${pack.slug}`}>
            <Link to="/growth-operator/blueprint">Build My Free Growth Blueprint <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
